import { useState } from 'react';
import { format, isValid, parseISO } from 'date-fns';
import { CalendarIcon, XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { DateRange } from 'react-day-picker';

import { cn } from '@/lib/utils';
import { dateLocaleFor } from '@/i18n';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { FieldError, FieldLabel } from './FormField';

/**
 * The date analogue of {@link TextField}: a string-in/string-out control so the composer's editor
 * state can keep every field a plain `"YYYY-MM-DD"` string. Internally it materializes a `Date`
 * for the calendar and formats it back on select — the surrounding form never sees a `Date`.
 */

/**
 * Parses a `"YYYY-MM-DD"` value to a local `Date`. `parseISO` reads a date-only string as *local*
 * midnight (not UTC), so the day never shifts across time zones. Empty or malformed input yields
 * `undefined` — the calendar shows nothing selected.
 */
function parseDateValue(value: string): Date | undefined {
  if (value.trim() === '') return undefined;
  const parsed = parseISO(value);
  return isValid(parsed) ? parsed : undefined;
}

/**
 * The "×" that empties a filled date field. It sits beside the popover trigger (overlaid on its right
 * edge) rather than inside it — a button nested in a button is invalid and would open the popover on
 * click. Callers render it only for an optional, enabled field that has a value.
 */
function ClearDateButton({ label, onClear }: { label: string; onClear: () => void }) {
  const { t } = useTranslation();
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon-xs"
      aria-label={t('common.clear_field', { field: label })}
      onClick={onClear}
      className="absolute top-1/2 right-1 -translate-y-1/2 text-muted-foreground"
    >
      <XIcon />
    </Button>
  );
}

export function DateField({
  id,
  label,
  required,
  error,
  value,
  onChange,
  disabled,
  placeholder,
  className,
}: {
  id: string;
  label: string;
  required?: boolean;
  error?: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const locale = dateLocaleFor(i18n.language);
  const selected = parseDateValue(value);
  const clearable = !required && !disabled && value.trim() !== '';

  return (
    <div className={cn('space-y-1.5', className)}>
      <FieldLabel htmlFor={id} required={required}>
        {label}
      </FieldLabel>
      <div className="relative">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              id={id}
              type="button"
              variant="outline"
              disabled={disabled}
              data-empty={selected ? undefined : true}
              aria-required={required || undefined}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? `${id}-error` : undefined}
              className={cn(
                'w-full justify-start px-2.5 font-normal data-[empty=true]:text-muted-foreground',
                clearable && 'pr-8',
              )}
            >
              <CalendarIcon className="text-muted-foreground" />
              {selected
                ? format(selected, 'd MMM yyyy', { locale })
                : (placeholder ?? t('common.pick_date'))}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={selected}
              defaultMonth={selected}
              onSelect={(day) => {
                onChange(day ? format(day, 'yyyy-MM-dd') : '');
                setOpen(false);
              }}
              locale={locale}
              autoFocus
            />
          </PopoverContent>
        </Popover>
        {clearable && <ClearDateButton label={label} onClear={() => onChange('')} />}
      </div>
      <FieldError id={`${id}-error`} message={error} />
    </div>
  );
}

/**
 * A two-ended date range in one popover, for a "from / to" span (e.g. the sale period). Same
 * string-in/string-out contract as {@link DateField}, but the value is a `{ from, to }` pair of
 * `"YYYY-MM-DD"` strings so the caller keeps mapping straight onto two separate fields. Either end
 * may be blank; the popover stays open until both are chosen.
 */
export function DateRangeField({
  id,
  label,
  required,
  error,
  value,
  onChange,
  disabled,
  placeholder,
  className,
}: {
  id: string;
  label: string;
  required?: boolean;
  error?: string;
  value: { from: string; to: string };
  onChange: (value: { from: string; to: string }) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const locale = dateLocaleFor(i18n.language);
  const from = parseDateValue(value.from);
  const to = parseDateValue(value.to);
  const fmt = (date: Date) => format(date, 'd MMM yyyy', { locale });
  const display =
    from && to
      ? `${fmt(from)} – ${fmt(to)}`
      : from
        ? `${fmt(from)} – …`
        : to
          ? `… – ${fmt(to)}`
          : null;
  const clearable = !required && !disabled && (value.from.trim() !== '' || value.to.trim() !== '');

  return (
    <div className={cn('space-y-1.5', className)}>
      <FieldLabel htmlFor={id} required={required}>
        {label}
      </FieldLabel>
      <div className="relative">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              id={id}
              type="button"
              variant="outline"
              disabled={disabled}
              data-empty={display ? undefined : true}
              aria-required={required || undefined}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? `${id}-error` : undefined}
              className={cn(
                'w-full justify-start px-2.5 font-normal data-[empty=true]:text-muted-foreground',
                clearable && 'pr-8',
              )}
            >
              <CalendarIcon className="text-muted-foreground" />
              {display ?? placeholder ?? t('common.pick_date')}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="range"
              selected={from || to ? { from, to } : undefined}
              defaultMonth={from ?? to}
              numberOfMonths={2}
              onSelect={(range: DateRange | undefined) => {
                onChange({
                  from: range?.from ? format(range.from, 'yyyy-MM-dd') : '',
                  to: range?.to ? format(range.to, 'yyyy-MM-dd') : '',
                });
                if (range?.from && range.to) setOpen(false);
              }}
              locale={locale}
              autoFocus
            />
          </PopoverContent>
        </Popover>
        {clearable && (
          <ClearDateButton label={label} onClear={() => onChange({ from: '', to: '' })} />
        )}
      </div>
      <FieldError id={`${id}-error`} message={error} />
    </div>
  );
}
